import { useMemo } from 'react';
import { useRoute } from '@react-navigation/native';

import { scale } from '@components/frame';
import { convertTimeLetter, isPhone } from '@helpers';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { PopupProps, UseManagePopupReturn } from '@components/machines/components/eventsChart/types';

export const calculatePopupPosition = (
  selectedIndex: number,
  barBlockWidth: number,
  popupWidth: number,
  chartWidth: number,
): { popupLeft: number; lineLeft: number } => {
  const lineLeft = selectedIndex * barBlockWidth + barBlockWidth / 2;
  const maxPopupLeft = chartWidth - popupWidth;

  let popupLeft = lineLeft - popupWidth / 2;

  if (popupLeft > maxPopupLeft) {
    popupLeft = maxPopupLeft;
  }

  if (popupLeft < 0) {
    popupLeft = 0;
  }

  return { popupLeft, lineLeft };
};

export const calculateTimeDisplay = (
  selectedItem: PopupProps['selectedItem'],
  isActive: boolean,
  isDay: boolean,
): Record<string, number | string> => {
  const time = convertTimeLetter(isActive ? selectedItem.active : selectedItem.productive);

  if (isDay) {
    time.m = Number(time.m) + Number(time.h) * 60;
  }

  return time;
};

const useManagePopup = ({
  C,
  selectedItem,
  data: { dataBar = [] },
  selectedIndex,
  toGo,
  isDay,
  id,
  fullWidth,
  isActive,
}: PopupProps): UseManagePopupReturn => {
  const route = useRoute();

  const loaded = dataBar?.length;
  const chartWidth = isPhone ? fullWidth - scale(60, false) : scale(473, false);
  const chartHeight = scale(119, false);
  const popupWidth = scale(170);
  const barBlockWidth = chartWidth / (dataBar.length || 1);

  const { popupLeft, lineLeft } = calculatePopupPosition(selectedIndex, barBlockWidth, popupWidth, chartWidth);

  const time = calculateTimeDisplay(selectedItem, isActive, isDay);

  const linkConfig = toGo(selectedItem, true);

  const colorPattern: Record<string, string> = {
    [MachinesScreen.ACTIVE]: C('Status/Light/Active'),
    [MachinesScreen.PRODUCTIVE]: C('Status/Light/Productive'),
  };

  const [name, color] = useMemo(() => {
    return Object.entries(colorPattern).find(([pattern]) => route.name.includes(pattern)) ?? ['', ''];
  }, [route.name]);

  // The drill-down (togo) arrow is hidden on the Day period — there is nothing to drill into (SF-5197)
  const showToGoButton = !!id && !isDay;

  return {
    loaded,
    chartWidth,
    chartHeight,
    popupWidth,
    popupLeft,
    lineLeft,
    time,
    isDay,
    name,
    color,
    linkConfig,
    showToGoButton,
  };
};

export default useManagePopup;
